import './order_wizard.css'

import { LoadingOutlined } from '@ant-design/icons'
import { useOrders } from '@api/useOrders'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { modes, paneModes } from '@constants/orderWizard'
import { ErrorNotification, Horizontal, NotificationMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { Drawer } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import api from '@utils/api'
import { useDestinationsContext } from './Contexts/DestinationsContext'
import { useOrderContainersContext } from './Contexts/OrderContainersContext'
import { useSupplyOptionContext } from './Contexts/SupplyOptionContext'
import { useWizardMaster } from './Contexts/WizardMasterContext'
import { EntryStatus } from './Footer/EntryStatus'
import { OrderSteps } from './Footer/Steps'
import { ControlPane } from './Panes/ControlPane'
import { DisplayPane } from './Panes/DisplayPane'
import { EditAlertContainer } from './Panes/DisplayPane/DisplayComponents/EditedAlert/EditedAlert'
import { addFlagsToSupply } from './util'
import dayjs from 'dayjs'

export function OrderWizard({
  closeModal,
  showingModal,
  selectedOrder,
  order,
  selectedRequestedOrder,
  resetTimeoutDelay = 1000,
}) {
  const { scopes } = useSecurityContext()
  const canEditCarrierWindow = useMemo(() => !!scopes?.scopes.includes('o.w'), [scopes])
  const { useOrderByIdQuery, resetOrderMutation } = useOrders()
  const { data: orderData, isFetching: isLoadingOrder } = useOrderByIdQuery(selectedOrder)
  const [orderFinishedLoading, setOrderFinishedLoading] = useState(false)
  const {
    wizardOrder,
    setWizardOrder,
    mode,
    paneMode,
    cancelOrder,
    setOrderStops,
    setMode,
    setPaneMode,
    setIsComplete,
    initializeSelectedStatus,
    setIsBackhaul,
    isBackhaul,
    setIsEditingBackhaul,
    createBackhaulPayload,
    setIsShortLoad,
    setOrderEdited,
    setPayload,
    setDriverNote,
    carrierWindowState,
    form,
    setIsOrderChanged,
  } = useWizardMaster()

  const {
    storesLoading,
    orderDestinations,
    setUpDestinations,
    setOrderDestinations,
    setSelectedTank,
    setSelectedManifold,
    defaultStore,
    resetDefaultStore,
  } = useDestinationsContext()

  const {
    setSelectedSupply,
    setNonOptimalChosen,
    setIncludeAlternates,
    setSelectedStatus,
    setSupplyOptions,
    setSupplyLoading,
    setPayload: setSupplyOptionPayload,
    onlyInNetwork,
    onlySingleSupplier,
    excludeOutages,
    includeAlternates,
    resetOnlySingleSupplierFilter,
    // Used to initialize supply filters from existing order requests for multi-origin/destination orders
    setInitialSupplyFilters,
  } = useSupplyOptionContext()
  const {
    constructCompartments,
    updateVolumes,
    setTrailerOverride,
    setHasChangedTrailer,
    resetCompartments,
    setTrailerConfigPayload,
    productGroupsApi,
    setTrimmedDefaults,
  } = useOrderContainersContext()

  let setPrefillStore

  try {
    setPrefillStore = useSiteMonitoringContext()?.setPrefillStore
  } catch (_) {}

  const queryClient = useQueryClient()

  useEffect(() => {
    const setupExistingOrder = async () => {
      if (orderData.is_by_product) {
        productGroupsApi.setProductGroupsFromCompartments(orderData?.compartments)
      }
      setPaneMode(paneModes.TANKIMPACT)
      setNonOptimalChosen(false)
      setMode(
        orderData?.carrier_window_eligible && canEditCarrierWindow && orderData.state !== 'complete'
          ? modes.EDIT
          : modes.VIEW
      )

      if (orderData?.type?.toLowerCase() === 'backhaul') {
        setIsBackhaul(true)
        createBackhaulPayload(orderData)
      } else {
        setSelectedTank(undefined)
        setIsShortLoad(orderData.is_short_load)
        constructCompartments(orderData)
        setSelectedSupply(addFlagsToSupply(orderData))
        initializeSelectedStatus(orderData, setSelectedStatus)
      }
      setDriverNote(orderData.note?.content)
      form.setFieldsValue({ content: orderData.note?.content })
      setWizardOrder(orderData)
      carrierWindowState.initializeWindowState(orderData, orderDestinations?.[0]?.timezone)
      setOrderStops(orderData.map_stops)

      const [firstStore] = await setUpDestinations(orderData)
      setOrderFinishedLoading(true)

      if (firstStore?.trailer_config_min_volume && firstStore?.trailer_config_max_volume) {
        productGroupsApi.setMinimumVolume(firstStore.trailer_config_min_volume)
        productGroupsApi.setMaximumVolume(firstStore.trailer_config_max_volume)
      }
    }

    if (orderData && selectedOrder) {
      setupExistingOrder()
    } else if (defaultStore) {
      if (defaultStore?.trailer_config_min_volume && defaultStore?.trailer_config_max_volume) {
        productGroupsApi.setMinimumVolume(defaultStore.trailer_config_min_volume)
        productGroupsApi.setMaximumVolume(defaultStore.trailer_config_max_volume)
      }
      setPaneMode(paneModes.PRODUCTSELECT)
      setUpDestinations()
    }
  }, [orderData, defaultStore])

  // For assigning manual supply to a delivery request.
  // TODO: Should refactor this into the rest of the order setup flow above
  useEffect(() => {
    if (order && !orderFinishedLoading) {
      if (order.is_by_product) {
        productGroupsApi.setProductGroupsFromCompartments(order?.compartments)
      }

      order.carrier_window_start = order.delivery_window_start
      order.carrier_window_end = order.delivery_window_end
      order.carrier_window_override = !!order.delivery_window_start && !!order.delivery_window_end
      if (selectedRequestedOrder) {
        order.order_request_info = selectedRequestedOrder?.order_request_info
      }
      setDriverNote(order.note?.content)
      form.setFieldsValue({ content: order.note?.content })
      setWizardOrder(order)
      carrierWindowState.initializeWindowState(order, orderDestinations?.[0]?.timezone)
      setPaneMode(paneModes.SUPPLYFILTERS)
      setUpDestinations(order).then(([firstStore]) => {
        if (firstStore?.trailer_config_min_volume && firstStore?.trailer_config_max_volume) {
          productGroupsApi.setMinimumVolume(firstStore.trailer_config_min_volume)
          productGroupsApi.setMaximumVolume(firstStore.trailer_config_max_volume)
        }
      })
      constructCompartments(order)
      const asOf = dayjs(order.delivery_window_start).isAfter(dayjs())
        ? order.delivery_window_start
        : dayjs().toISOString()
      setSupplyOptionPayload({
        pockets: order.compartments,
        destinations: order.compartments.flatMap((c) => c.targets.map((t) => t.location_name)),
        only_in_network: onlyInNetwork,
        use_alternates: includeAlternates,
        single_supplier_per_load: onlySingleSupplier,
        exclude_outages: excludeOutages,
        supply_owner_map: order.supply_owner_map,
        as_of: asOf,
      })
      if (order.supply_option_filters) {
        setInitialSupplyFilters(order.supply_option_filters)
      }

      setSupplyOptions(order.supply_options)
      setOrderFinishedLoading(true)
    }
  }, [order, selectedRequestedOrder])

  const closeAndClear = (isReload, newOrder, isScheduleReload) => {
    // Reset Order And Close Modal
    setIsOrderChanged(false)
    closeModal(isReload, newOrder, isScheduleReload)
    setTrailerOverride(undefined)
    setTrailerConfigPayload(undefined)
    setTrimmedDefaults([])
    resetOnlySingleSupplierFilter()

    if (setPrefillStore) {
      setPrefillStore(undefined)
      setOrderDestinations([])
    }

    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['getOrderByIdQuery', wizardOrder?.id || selectedOrder],
      })
      setIsComplete(false)
      setSupplyLoading(false)
      setSupplyOptionPayload(undefined)
      if (defaultStore) {
        resetDefaultStore(updateVolumes)
      } else {
        setOrderDestinations([])
      }
      setHasChangedTrailer(false)
      setPayload(undefined)
      setSelectedTank(undefined)
      setSelectedManifold(undefined)
      setIsBackhaul(false)
      setIsEditingBackhaul(false)
      setSelectedSupply(undefined)
      setSupplyOptions(undefined)
      setOrderEdited(false)
      setIsShortLoad(false)
      setDriverNote(undefined)
      setIncludeAlternates(false)
      resetCompartments()
      carrierWindowState.clearAndReset()
      setNonOptimalChosen(false)
      setWizardOrder(undefined)
      setSelectedStatus('')
      setMode(modes.CREATE)
      setPaneMode(paneModes.PRODUCTSELECT)
      setUpDestinations(null, updateVolumes)
      setOrderFinishedLoading(false)
      productGroupsApi.resetProductGroups()
    }, resetTimeoutDelay)
  }

  const closeAndCancel = async (isComplete, note) => {
    if (isComplete) {
      NotificationMessage('Cannot Cancel', 'Completed orders cannot be canceled.')
      return
    }
    await cancelOrder(note)
    closeAndClear(true)
  }
  const closeAndReset = async () => {
    const hasBeenExported = await api.checkExportStatus({ order_id: wizardOrder?.id })
    if (hasBeenExported.data === true) {
      ErrorNotification({
        errorFields: [
          {
            errors: [`This order has already been exported and cannot be reset.`],
          },
        ],
      })
      return
    }
    await resetOrderMutation.mutateAsync({ order_id: wizardOrder.id })
    closeAndClear(true)
  }

  const loading =
    storesLoading ||
    (((!orderDestinations.length > 0 && !orderFinishedLoading && !isBackhaul) || (isBackhaul && isLoadingOrder)) &&
      !!selectedOrder)
  return (
    <Drawer
      destroyOnClose
      placement='bottom'
      open={showingModal}
      onClose={() => closeAndClear(false)}
      title='Order Wizard'
      className='order-detail-modal flex'
      width={'99vw'}
      height={'98vh'}
    >
      {loading ? (
        <div className='loading-container vertical-flex-center' style={{ height: '100%' }}>
          <Texto style={{ fontSize: '6em' }} category='h1' appearance='secondary'>
            <LoadingOutlined />
          </Texto>
          <Texto className='mt-2' category='heading' appearance='medium'>
            Loading Order Info
          </Texto>
        </div>
      ) : (
        <>
          <Horizontal flex={1} style={{ minWidth: '1440px' }}>
            <Vertical fullHeight flex={4} className='full-height-col'>
              <ControlPane cancelOrder={closeAndCancel} resetOrder={closeAndReset} />
            </Vertical>
            <Vertical flex={17} className='full-height-col'>
              <DisplayPane />
            </Vertical>
          </Horizontal>
          <EditAlertContainer canEditCarrierWindow={canEditCarrierWindow} />
          <OrderFooter paneMode={paneMode} mode={mode} closeModal={closeAndClear} wizardOrder={wizardOrder} />
        </>
      )}
    </Drawer>
  )
}

function OrderFooter({ paneMode, mode, closeModal, wizardOrder }) {
  if (mode === modes.VIEW) {
    return <OrderSteps order={wizardOrder} closeModal={closeModal} />
  }
  return <EntryStatus paneMode={paneMode} mode={mode} closeModal={closeModal} />
}
